<template>
  <RevForm
    :initial-values
    :name="FORM_ID"
    :validate
    @submit="handleSubmit"
    @visible-errors-change="handleFormUnvalidate"
  >
    <template #default="{ values }">
      <RevDrawer
        :name="MODAL_NAME"
        size="large"
        :title="
          i18n(translations.drawerTitle, {
            categoryGroup: data.categoryGroup.name,
          })
        "
        @open="handleDrawerOpen"
      >
        <template #body>
          <div class="space-y-24">
            <RevStepper
              :active-step="activeStep.name"
              alternative-text-back="back"
              alternative-text-close="close"
              alternative-text-completed="completed"
              :alternative-text-current="activeStep.label"
              :has-back="!!currentStepIndex"
              :steps
              @back="handlePreviousStep"
            />

            <template v-if="activeStep.name === STEP_NAMES.CATEGORY_SELECTION">
              <RevCard class="p-24">
                <TreeSelect
                  v-model="values.categories"
                  :available-options="categoryOptions"
                >
                  <template #checkboxAll>
                    {{ i18n(translations.allCategories) }}
                  </template>
                </TreeSelect>
              </RevCard>
            </template>

            <template v-if="activeStep.name === STEP_NAMES.DELIVERY_DEADLINE">
              <RevCard v-if="preparationTime" class="space-y-24 p-24">
                <div class="flex">
                  <IconClock class="mr-8" />
                  <div>
                    <span class="body-1-bold">
                      {{
                        i18n(translations.preparationTimeTitle, {
                          time: getFormatHoursInDay(preparationTime),
                        })
                      }}
                    </span>
                  </div>
                </div>
              </RevCard>

              <RevCard class="space-y-24 p-24">
                <div class="flex">
                  <IconTruck class="mr-8" />
                  <div>
                    <span class="body-1-bold">
                      {{ freeShippingTitle }}
                    </span>
                    <p class="body-2">
                      {{ i18n(translations.freeShippingDescription) }}
                    </p>
                  </div>
                </div>

                <RuleFormFields
                  key="free-form-fields"
                  :available-markets="props.availableMarkets"
                  :category-group-metadata
                  :current-methods="values.freeMethods"
                  is-free
                  :is-free-mandatory
                  :other-methods="values.paidMethods"
                />
              </RevCard>

              <RevCard class="space-y-24 p-24">
                <div class="flex">
                  <IconTruck class="mr-8" />
                  <div>
                    <span class="body-1-bold">
                      {{ paidShippingTitle }}
                    </span>
                    <p class="body-2">
                      {{ i18n(translations.paidShippingDescription) }}
                    </p>
                  </div>
                </div>

                <RevInfoBlock
                  v-if="areFreeMethodsMissing(values)"
                  :icon="IconInfo"
                  :title="i18n(translations.freeMethodsNeeded)"
                  variant="default"
                />

                <RuleFormFields
                  v-if="!areFreeMethodsMissing(values)"
                  key="paid-form-fields"
                  :available-markets="getMarketsAvailableForPaidMethods(values)"
                  :category-group-metadata
                  :current-methods="values.paidMethods"
                  :is-free-mandatory
                  :other-methods="values.freeMethods"
                />
              </RevCard>

              <RevCard
                v-if="
                  shouldShowCollectionPointBlock({
                    carriers: props.shippingMethodsMetadata.providerOptions,
                  })
                "
                class="space-y-24 p-24"
              >
                <div class="flex">
                  <IconShop class="mr-8" />
                  <div>
                    <span class="body-1-bold">
                      {{ i18n(translations.collectionPointTitle) }}
                    </span>
                    <p class="body-2">
                      {{ i18n(translations.collectionPointDescription) }}
                    </p>
                  </div>
                </div>

                <RuleFormFields
                  key="collection-point-form-fields"
                  :available-markets="
                    getAvailableCollectionPointCarriers(
                      props.shippingMethodsMetadata.providerOptions,
                    )
                  "
                  :category-group-metadata
                  :current-methods="values.freeMethods"
                  is-free
                  :is-free-mandatory
                  :other-methods="values.paidMethods"
                  :should-show-collection-point-options="true"
                />
              </RevCard>
            </template>

            <template v-if="activeStep.name === STEP_NAMES.SUMMARY">
              <RevInfoBlock
                v-for="(err, i) in apiError"
                :key="i"
                class="mb-4"
                :content="err.detail"
                :icon="IconWarning"
                :title="err.title"
                variant="danger"
              />
              <RuleSummary
                :category-group="data.categoryGroup"
                :category-group-metadata
                :values
              />
            </template>
          </div>
        </template>

        <template #footer>
          <RevButton
            v-if="activeStep.name === STEP_NAMES.CATEGORY_SELECTION"
            :disabled="!canGoNext(values, activeStep.name)"
            full-width="always"
            variant="primary"
            @click="handleNextStep"
          >
            {{ i18n(translations.next) }}
          </RevButton>
          <RevButton
            v-if="activeStep.name === STEP_NAMES.DELIVERY_DEADLINE"
            :disabled="!canGoNext(values, activeStep.name)"
            full-width="always"
            variant="primary"
            @click="handleNextStep"
          >
            {{ i18n(translations.next) }}
          </RevButton>
          <RevButton
            v-if="activeStep.name === STEP_NAMES.SUMMARY"
            :disabled="isSubmitting"
            full-width="always"
            :loading="isSubmitting"
            type="submit"
            variant="primary"
            @click="handleSubmitForm"
          >
            {{ i18n(translations.save) }}
          </RevButton>
        </template>
      </RevDrawer>
    </template>
  </RevForm>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { computed, ref } from 'vue'

import type { Currency, MarketCountryCode } from '@backmarket/http-api'
import { getPreparationTime } from '@backmarket/http-api/src/api-specs-shipping/preparation-times'
import {
  type MarketWithSubMarkets,
  type PostShippingRuleData,
  postShippingRule,
} from '@backmarket/http-api/src/api-specs-shipping/rules'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevDrawer } from '@ds/components/Drawer'
import {
  FORM_VALID,
  RevForm,
  type Validator,
  makeValidate,
  resetForm,
} from '@ds/components/Form'
import { submitForm } from '@ds/components/Form/Form.actions'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { closeModal } from '@ds/components/ModalBase'
import { RevStepper } from '@ds/components/Stepper'
import { IconClock } from '@ds/icons/IconClock'
import { IconInfo } from '@ds/icons/IconInfo'
import { IconShop } from '@ds/icons/IconShop'
import { IconTruck } from '@ds/icons/IconTruck'
import { IconWarning } from '@ds/icons/IconWarning'

import { useFormatHours } from '~/composables/useFormatHours'

import { FORM_IDS, MODAL_NAMES } from '../../../../Shipping.constants'
import type { ShippingError } from '../../../../Shipping.types'
import { getApiErrorsMessage } from '../../../../Shipping.utils'
import TreeSelect from '../../../TreeSelect/TreeSelect.vue'
import type {
  RuleDrawerData,
  ShippingCategoryGroupMetadata,
  ShippingMethodsMetadata,
} from '../../CategoryList.types'
import { getCarrier } from '../../CategoryList.utils'

import { STEP_NAMES } from './RuleDrawer.constants'
import translations from './RuleDrawer.translations'
import type {
  DrawerStep,
  FormMethodValues,
  FormValues,
} from './RuleDrawer.types'
import {
  formatMethod,
  getAvailableCollectionPointCarriers,
  isMethodValid,
  shouldShowCollectionPointBlock,
} from './RuleDrawer.utils'
import RuleFormFields from './components/RuleFormFields/RuleFormFields.vue'
import RuleSummary from './components/RuleSummary/RuleSummary.vue'
import { useHttpGetThresholds } from './composables/useHttpGetThresholds'

const MODAL_NAME = MODAL_NAMES.SHIPPING_RULES
const FORM_ID = FORM_IDS.SHIPPING_RULES

const props = defineProps<{
  sellerId: string
  availableMarkets: MarketWithSubMarkets[]
  data: RuleDrawerData
  shippingMethodsMetadata: ShippingMethodsMetadata
}>()

const emit = defineEmits(['form-update'])

const i18n = useI18n()
const { openSuccessToast, openErrorToast } = useTheToast()
const { getFormatHoursInDay } = useFormatHours()

// Needed for the API call, passing directly the prop breaks the reactivity
const groupId = computed(() => props.data.categoryGroup.id)

const [
  { data: preparationTime, error: preparationTimeError },
  { data: validationThresholds, error: validationThresholdsError },
] = await Promise.all([
  useHttpFetch(getPreparationTime, {
    pathParams: { sellerId: props.sellerId },
    queryParams: { shippingCategoryGroupId: groupId },
    transform: ({ preparationTimeHours: time }) => time,
    watch: [groupId],
  }),
  useHttpGetThresholds(
    groupId,
    ref(
      props.availableMarkets.map(({ market }) => market as MarketCountryCode),
    ),
    ['METHODS_COUNT_THRESHOLDS'],
    false,
  ),
])

const error = computed(
  () => preparationTimeError.value || validationThresholdsError.value,
)

if (error.value) {
  openErrorToast({
    content: getApiErrorMessage(error.value as unknown as Error),
  })
}

const steps: DrawerStep[] = [
  {
    name: STEP_NAMES.CATEGORY_SELECTION,
    label: i18n(translations.categorySelection),
  },
  {
    name: STEP_NAMES.DELIVERY_DEADLINE,
    label: i18n(translations.deliveryDeadline),
  },
  {
    name: STEP_NAMES.SUMMARY,
    label: i18n(translations.summary),
  },
]

const currentStepIndex = ref(0)
const apiError = ref<ShippingError['errors']>()
const isSubmitting = ref(false)
const isFreeMandatory = computed(() => {
  return (validationThresholds.value?.methods.free.min || 0) > 0
})

const freeShippingTitle = computed(() =>
  isFreeMandatory.value
    ? i18n(translations.freeShippingTitle)
    : i18n(translations.freeShippingTitleOptional),
)

const paidShippingTitle = computed(() =>
  isFreeMandatory.value
    ? i18n(translations.paidShippingTitleOptional)
    : i18n(translations.paidShippingTitle),
)

const availableCategories = computed(() =>
  props.data.categoryGroup.shippingCategories.reduce((arr, { id }) => {
    if (!props.data.categories.includes(id)) {
      arr.push(id)
    }

    return arr
  }, props.data.rule?.categories.slice() || []),
)

const initialValues = computed<FormValues>(() => {
  return {
    ...props.data.rule,
    categories: props.data.rule?.categories || availableCategories.value,
    freeMethods:
      props.data.rule?.freeMethods.map((method) => formatMethod(method)) || [],
    paidMethods:
      props.data.rule?.paidMethods.map((method) => formatMethod(method)) || [],
  }
})

const categoryOptions = computed(() => {
  return props.data.categoryGroup.shippingCategories.map((category) => {
    const isAlreadyUsed = !availableCategories.value.includes(category.id)

    return {
      label: category.name,
      value: category.id,
      // Categories used for some other rules must be disabled
      disabled: isAlreadyUsed,
      appearsChecked: isAlreadyUsed,
      ...(category.categories.length <= 1 &&
      category.name === category.categories[0]?.name
        ? null
        : {
            description: category.categories.map(({ name }) => name).join(', '),
          }),
    }
  })
})

const activeStep = computed(() => steps[currentStepIndex.value])

const categoryGroupMetadata = computed<ShippingCategoryGroupMetadata>(() => ({
  id: props.data.categoryGroup.id,
  shippingMethods: props.shippingMethodsMetadata,
  preparationTime: preparationTime.value || 0,
}))

function areFreeMethodsMissing(values: FormValues) {
  return isFreeMandatory.value && !values.freeMethods.length
}

function allSubMarketsHaveFreeMethod(freeMethods: FormValues['freeMethods']) {
  if (!isFreeMandatory.value) return true

  const currentSubMarketWithFreeMethod = freeMethods
    .map((method) =>
      method.markets
        .map(({ subMarkets }) => {
          if (subMarkets.length) {
            return subMarkets.map((subMarket) => subMarket.subMarketCode)
          }

          return []
        })
        .flat(),
    )
    .flat()

  return props.availableMarkets.every(({ subMarkets }) => {
    if (subMarkets.length) {
      return subMarkets.every((subMarket) =>
        currentSubMarketWithFreeMethod.includes(subMarket.subMarketCode),
      )
    }

    return true
  })
}

function canGoNext(values: FormValues, step: DrawerStep['name']) {
  return {
    [STEP_NAMES.CATEGORY_SELECTION]: !!values.categories.length,
    [STEP_NAMES.DELIVERY_DEADLINE]:
      values.categories.length &&
      !areFreeMethodsMissing(values) &&
      allSubMarketsHaveFreeMethod(values.freeMethods) &&
      values.freeMethods.length + values.paidMethods.length > 0,
    [STEP_NAMES.SUMMARY]: true,
  }[step]
}

function mergeMarkets(marketsWithSubMarkets: MarketWithSubMarkets[]) {
  const mergedMarketsWithSubMarketsObject: Partial<
    Record<MarketCountryCode, MarketWithSubMarkets>
  > = {}

  marketsWithSubMarkets.forEach((marketWithSubMarket) => {
    const existingMarket =
      mergedMarketsWithSubMarketsObject[marketWithSubMarket.market]

    if (!existingMarket) {
      mergedMarketsWithSubMarketsObject[marketWithSubMarket.market] = {
        market: marketWithSubMarket.market,
        subMarkets: [...marketWithSubMarket.subMarkets],
      }
    } else {
      existingMarket.subMarkets.push(...marketWithSubMarket.subMarkets)
    }
  })

  return Object.values(mergedMarketsWithSubMarketsObject)
}

function getMarketsAvailableForPaidMethods(values: FormValues) {
  return isFreeMandatory.value
    ? mergeMarkets(values.freeMethods.flatMap(({ markets }) => markets))
    : props.availableMarkets
}

function handlePreviousStep() {
  apiError.value = []
  currentStepIndex.value = Math.max(0, currentStepIndex.value - 1)
}

function handleNextStep() {
  currentStepIndex.value = Math.min(steps.length, currentStepIndex.value + 1)
}

// At least N free methods required (N from thresholds)
// Every method (free or paid) must be valid
// TODO translations? - even tho it shouldnt be a user-facing error
function validateMethods(isFree: boolean) {
  return (methods: FormMethodValues[], formValues: FormValues) =>
    (methods.length || !isFree || !isFreeMandatory.value) &&
    methods.every((method) =>
      isMethodValid(
        method,
        isFree ? formValues.freeMethods : formValues.paidMethods,
        isFree ? formValues.paidMethods : formValues.freeMethods,
        isFree,
        isFreeMandatory.value,
        1,
      ),
    )
      ? FORM_VALID
      : 'error'
}

const validate = makeValidate<FormValues>({
  categories: [
    ((categories: FormValues['categories']) =>
      categories.length ? FORM_VALID : 'error') as Validator<FormValues>,
  ],
  freeMethods: [validateMethods(true) as Validator<FormValues>],
  paidMethods: [validateMethods(false) as Validator<FormValues>],
})

function handleDrawerOpen() {
  currentStepIndex.value = 0
  apiError.value = []
  isSubmitting.value = false
  resetForm(FORM_ID)
}

async function handleSubmitForm() {
  submitForm(FORM_ID)
}

async function handleFormUnvalidate() {
  openErrorToast({
    title: i18n(translations.toastAddRuleErrorTitle),
    content: i18n(translations.toastAddRuleErrorContent),
  })
}

async function handleSubmit(values: FormValues) {
  apiError.value = []
  isSubmitting.value = true

  try {
    const body: PostShippingRuleData = {
      id: props.data.rule?.id,
      shippingCategoryGroupId: props.data.categoryGroup.id,
      shippingCategoryIds: values.categories,
      deliveryModes: [],
    }

    // Add free methods if needed
    if (values.freeMethods.length) {
      const freeMethods = values.freeMethods.filter(({ shippingProvider }) => {
        const carrier = getCarrier(
          props.shippingMethodsMetadata.providerOptions,
          shippingProvider,
        )

        return !carrier?.collectionPoint
      })

      const collectionPointMethods = values.freeMethods.filter(
        ({ shippingProvider }) => {
          const carrier = getCarrier(
            props.shippingMethodsMetadata.providerOptions,
            shippingProvider,
          )

          return carrier?.collectionPoint
        },
      )

      if (freeMethods.length) {
        body.deliveryModes.push({
          deliveryMode: 'HOME_DELIVERY_FREE',
          deliveryMethods: freeMethods.map((method) => ({
            transitTime: +method.shippingTime,
            carrierCodename: method.shippingProvider,
            markets: method.markets.map((market) => ({
              market: market.market,
              subMarkets: market.subMarkets.map(
                (subMarket) => subMarket.subMarketCode,
              ),
            })),
          })),
        })
      }

      if (collectionPointMethods.length) {
        body.deliveryModes.push({
          deliveryMode: 'COLLECTION_POINT_DELIVERY_FREE',
          deliveryMethods: collectionPointMethods.map((method) => ({
            transitTime: +method.shippingTime,
            carrierCodename: method.shippingProvider,
            markets: method.markets.map((market) => ({
              market: market.market,
              subMarkets: market.subMarkets.map(
                (subMarket) => subMarket.subMarketCode,
              ),
            })),
          })),
        })
      }
    }

    // Add paid methods if needed
    if (values.paidMethods.length) {
      body.deliveryModes.push({
        deliveryMode: 'HOME_DELIVERY_PAID',
        deliveryMethods: values.paidMethods.map((method) => ({
          transitTime: +method.shippingTime,
          carrierCodename: method.shippingProvider,
          shippingFee: {
            amount: method.price.toString(),
            currency: method.currency as Currency,
          },
          markets: method.markets.map((market) => ({
            market: market.market,
            subMarkets: market.subMarkets.map(
              (subMarket) => subMarket.subMarketCode,
            ),
          })),
        })),
      })
    }

    await $httpFetch(postShippingRule, {
      pathParams: { sellerId: props.sellerId },
      body,
    })

    openSuccessToast({
      title: i18n(translations.toastAddRuleSuccessTitle),
      content: i18n(translations.toastAddRuleSuccessContent),
    })
    emit('form-update')
    closeModal(MODAL_NAME)
  } catch (err) {
    apiError.value = getApiErrorsMessage(err as ShippingError)
  } finally {
    isSubmitting.value = false
  }
}
</script>
